import api, { ClientInvestor, ExclusionOverlay, InvestorStatus } from '@api';
import { Button, Typography } from '@mui/material';
import { FilterTextField, FilterableFilledSection, RoutedDialogManager } from '@tsp-ui/core/components';
import { useAsyncEffect, usePageMessage } from '@tsp-ui/core/utils';
import ExclusionOverlaySection from '@views/admin/investor-eligibility/components/ExclusionOverlaySection';
import Page from '@views/components/Page';
import {
    Dispatch, SetStateAction, createContext, useCallback, useContext, useState
} from 'react';
import { Link } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import styles from './InvestorEligibilityManagementPage.module.scss';
import { InvestorEligibilityContext } from './InvestorEligibilityRoutes';
import { ClientInvestorCard } from './components/ClientInvestorCard';
import { overlayDialogRoutes } from './overlayDialogRoutes';


export interface ExclusionOverlayContextValue {
    overlays: ExclusionOverlay[];
    setOverlays: Dispatch<SetStateAction<ExclusionOverlay[]>>
}

export const ExclusionOverlayContext = createContext<ExclusionOverlayContextValue>({
    overlays: [],
    setOverlays: () => {}
});

export default function InvestorEligibilityManagementPage() {
    const pageMessage = usePageMessage();

    const [ loading, setLoading ] = useState(true);
    const [ showInactive, setShowInactive ] = useState(false);
    const [ searchTerm, setSearchTerm ] = useState('');
    const [ debouncedTerm ] = useDebounce(searchTerm, 300);

    const { investors } = useContext(InvestorEligibilityContext);
    const filteredInvestors = investors?.filter(({ name, status }) => (
        name.toLocaleLowerCase().includes(debouncedTerm) && (showInactive || status !== InvestorStatus.INACTIVE)
    )).sort(sortAlphabeticallyByStatus);

    const [ overlays, setOverlays ] = useState<ExclusionOverlay[]>([]);

    useAsyncEffect(useCallback(async () => {
        setLoading(true);

        try {
            setOverlays(await api.overlay.getExclusionOverlays());
        } catch (error) {
            pageMessage.handleApiError('An error occurred while fetching exclusion overlays', error);
        }

        setLoading(false);
    }, [ pageMessage ]));

    return (
        <ExclusionOverlayContext.Provider
            value={{
                overlays,
                setOverlays
            }}
        >
            <Page
                header="Investor Eligibility Management"
                loading={loading || !investors}
            >
                <RoutedDialogManager routes={overlayDialogRoutes} />

                <div className={styles.root}>
                    <FilterableFilledSection
                        header={(
                            <>
                                Investors

                                <Button
                                    className={styles.disabledCheckbox}
                                    onClick={() => setShowInactive(!showInactive)}
                                >
                                    {showInactive ? 'Hide' : 'Show'} inactive
                                </Button>
                            </>
                        )}
                        addButton={(
                            <Button
                                variant="contained"
                                component={Link}
                                to="investors/add"
                            >
                                Add Investor
                            </Button>
                        )}
                        filterField={(
                            <FilterTextField
                                autoFocus
                                placeholder="Filter investors"
                                helperText="Filter by name"
                                onChange={(event) => setSearchTerm(event.target.value.toLocaleLowerCase())}
                            />
                        )}
                    >
                        {filteredInvestors?.length ? filteredInvestors.map(investor => (
                            <ClientInvestorCard
                                key={investor.id}
                                investor={investor}
                            />
                        )) : (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                            >
                                No investors are currently configured
                            </Typography>
                        )}
                    </FilterableFilledSection>

                    <ExclusionOverlaySection overlays={overlays} />
                </div>
            </Page>
        </ExclusionOverlayContext.Provider>
    );
}

function sortAlphabeticallyByStatus(investorA: ClientInvestor, investorB: ClientInvestor) {
    if (investorA.status === investorB.status) {
        // sort alphabetically
        return investorA.name.localeCompare(investorB.name);
    } else if (investorB.status === InvestorStatus.INACTIVE) {
        // sort A first
        return -1;
    } else if (investorB.status === InvestorStatus.ACTIVE) {
        // sort B first
        return 1;
    } else if (investorA.status === InvestorStatus.ACTIVE) {
        // if B is pending and A is active, sort A first
        return 1;
    } else {
        // if B is pending and A is inactive, sort B first
        return -1;
    }
}
